import { icons } from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import type { ComponentType } from 'react';
import './primitives.css';

/**
 * Foundation primitive: Icon（Phase D §4.3 / Phase B 凍結対象 API）
 *
 * lucide-react（player の既存依存）の薄いラッパー。新規依存は入れない。
 * - name: lucide のアイコン名（PascalCase, 例 'Search'）。型補完が効く。
 * - icon: lucide コンポーネントを直接渡す経路（name の代替）。
 * - size: px。既定 20。
 * 色はトークン由来で制御する＝lucide は stroke に currentColor を使うため、
 * 親要素の color（--mg-fg / --mg-accent 等）を継承する。色 prop は持たせない
 * （hex 直書きを避け、配色はトークン側に一本化するため）。
 */
export type IconName = keyof typeof icons;

interface IconBaseProps extends Omit<LucideProps, 'ref' | 'size' | 'color'> {
  size?: number;
}

export interface IconProps extends IconBaseProps {
  /** lucide アイコン名（name か icon のどちらかを指定） */
  name?: IconName;
  /** lucide コンポーネントを直接渡す（name の代替） */
  icon?: ComponentType<LucideProps>;
}

export function Icon({ name, icon, size = 20, className, ...rest }: IconProps) {
  // lucide の icons レコードの値型は ForwardRefExoticComponent 直で、現行の
  // @types/react 下では JSX 要素型として弾かれる（TS2786）。React 標準の
  // ComponentType<LucideProps> へ正規化して描画する（新規依存は足さない）。
  const resolved = (icon ?? (name ? icons[name] : undefined)) as
    | ComponentType<LucideProps>
    | undefined;
  if (!resolved) return null;

  const LucideIcon = resolved;
  const classes = ['mg-icon', className].filter(Boolean).join(' ');
  return <LucideIcon size={size} className={classes} aria-hidden {...rest} />;
}
